import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Dialog } from '@mui/material';
import {
  DialogActionsImg,
  DialogActionsProductBox,
  DialogActionsProductButton,
  DialogActionsProductConfirmButton,
  DialogActionsTypography,
  DialogContentBox,
  DialogPageProps,
  DialogTextProvider,
  DialogTextSubProvider,
} from './styles';
import DraggableDialogWrapper from './DraggableDialogWrapper';
import {
  ButtonTexts,
  EventActionEnum,
  EventServiceSourceEnum,
  EventStatusesEnum,
  EventTypeIcons,
  ModalOpenStatusEnum,
  SET_MODAL_ACTIONS,
  SET_PRODUCT_EVENTS,
  SET_PRODUCT_EVENTS_NEXT_LOCATION,
} from './constants';
import { updateCachingEvent } from './service';
import useQuery from '../../hooks/useQuery';

const ConfirmProductActions = ({ formData, handleSubmit, setIsPriceDirty = () => {} }) => {
  const dispatch = useDispatch();
  const query = useQuery();
  const mode = query.get('mode');
  const history = useHistory();
  const { productEvents } = useSelector((state) => state);
  const [isOpen, setOpen] = useState(false);
  const eventData = productEvents?.eventData;
  const event = {};
  eventData?.forEach((element) => {
    Object.assign(event, element);
  });

  const title = event?.title;
  const message = event?.message;
  const type = event?.type;
  const uuid = event?.uuid;

  useEffect(() => {
    if (eventData?.length) {
      updateCachingEvent(uuid, {
        status: EventStatusesEnum.showed,
        serviceSource: EventServiceSourceEnum.partners,
      });
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [eventData, uuid]);

  const handleCancel = () => {
    setIsPriceDirty(false);
    setOpen(false);
    dispatch({ type: SET_MODAL_ACTIONS, payload: ModalOpenStatusEnum.cancel });
    updateCachingEvent(uuid, {
      status: EventStatusesEnum.canceled,
      serviceSource: EventServiceSourceEnum.partners,
      action: EventActionEnum.canceled,
    });
    dispatch({ type: SET_PRODUCT_EVENTS, payload: [] });
    dispatch({
      type: SET_PRODUCT_EVENTS_NEXT_LOCATION,
      payload: `${history.location.pathname}${history.location.search}`,
    });
    localStorage.removeItem('SET_MODAL_ACTIONS');
  };

  const handleConfirm = (action) => {
    setOpen(false);
    // Clear events immediately to prevent duplicate modal
    dispatch({ type: SET_PRODUCT_EVENTS, payload: [] });
    localStorage.removeItem('SET_MODAL_ACTIONS');
    setIsPriceDirty(false);
    updateCachingEvent(uuid, {
      status: EventStatusesEnum.confirmed,
      serviceSource: EventServiceSourceEnum.partners,
      action,
    }).then(() => {
      const isInOverview = history?.location.pathname?.includes('/overview');
      if (isInOverview) {
        dispatch({
          type: SET_MODAL_ACTIONS,
          payload: ModalOpenStatusEnum.confirm,
        });
      }
      if (formData) {
        const payload =
          typeof formData === 'function' ? formData() : formData;
        handleSubmit(payload).finally(() => {
          // Clear any events that might have been returned from the API call
          // to prevent duplicate modals, especially for zero price confirmation
          dispatch({ type: SET_PRODUCT_EVENTS, payload: [] });

          if (mode === 'create') {
            dispatch({
              type: SET_PRODUCT_EVENTS_NEXT_LOCATION,
              payload: `/menu/overview`,
            });
            history.push('/menu/overview');
          } else {
            if (productEvents.nextLocation) {
              history.push(productEvents.nextLocation);
            } else {
              history.push({
                pathname: history.location.pathname,
                search: history.location.search,
                state: {},
              });
            }
          }
          dispatch({
            type: SET_MODAL_ACTIONS,
            payload: ModalOpenStatusEnum.confirm,
          });
        });
      } else {
        if (mode === 'create') {
          dispatch({
            type: SET_PRODUCT_EVENTS_NEXT_LOCATION,
            payload: `/menu/overview`,
          });
          history.push('/menu/overview');
        } else {
          if (productEvents.nextLocation) {
            history.push(productEvents.nextLocation);
          } else {
            history.push({
              pathname: history.location.pathname,
              search: history.location.search,
              state: {},
            });
          }
        }
        dispatch({
          type: SET_MODAL_ACTIONS,
          payload: ModalOpenStatusEnum.confirm,
        });
      }
    });
  };

  const renderActionButtons = () => {
    if (ButtonTexts[type]?.length > 1) {
      return (
        <>
          <DialogActionsProductButton variant="outlined" onClick={handleCancel}>
            <DialogActionsTypography color="#626A7A">
              {ButtonTexts[type]?.[0]}
            </DialogActionsTypography>
          </DialogActionsProductButton>
          <DialogActionsProductConfirmButton
            variant="contained"
            onClick={() =>
              handleConfirm(
                EventActionEnum[ButtonTexts[type]?.[1]?.toLowerCase()]
              )
            }
          >
            <DialogActionsTypography color="#fff">
              {ButtonTexts[type]?.[1]}
            </DialogActionsTypography>
          </DialogActionsProductConfirmButton>
        </>
      );
    } else {
      return (
        <DialogActionsProductConfirmButton
          variant="contained"
          onClick={() => handleConfirm(ButtonTexts[type]?.[0]?.toLowerCase())}
        >
          <DialogActionsTypography color="#fff">
            {ButtonTexts[type]?.[0]}
          </DialogActionsTypography>
        </DialogActionsProductConfirmButton>
      );
    }
  };

  const handleClose = (event, reason) => {
    setIsPriceDirty(false);
    setOpen(false);
    dispatch({ type: SET_MODAL_ACTIONS, payload: ModalOpenStatusEnum.close });
    if (reason) {
      updateCachingEvent(uuid, {
        status: EventStatusesEnum.canceled,
        serviceSource: EventServiceSourceEnum.partners,
        action: EventActionEnum.closed,
      });
      dispatch({ type: SET_PRODUCT_EVENTS, payload: [] });
      localStorage.removeItem('SET_MODAL_ACTIONS');
    }
  };

  return isOpen ? (
    <Dialog
      open={isOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      disableEscapeKeyDown
      aria-labelledby="draggable-dialog-content"
      fullWidth={true}
      PaperComponent={DraggableDialogWrapper}
      PaperProps={{
        sx: DialogPageProps,
      }}
    >
      <DialogContentBox id="draggable-dialog-content">
        <DialogActionsImg
          component="img"
          src={EventTypeIcons[type]}
          alt={type}
        />
        <DialogTextProvider>{title}</DialogTextProvider>
        {event?.contentType === 'text' ? (
          <DialogTextSubProvider>{message}</DialogTextSubProvider>
        ) : (
          <Box
            dangerouslySetInnerHTML={{
              __html: message,
            }}
          />
        )}
      </DialogContentBox>
      <DialogActionsProductBox>{renderActionButtons()}</DialogActionsProductBox>
    </Dialog>
  ) : (
    <></>
  );
};
export default ConfirmProductActions;
